import { contentRevision } from "game-common/content/catalog"
import { Application, UPDATE_PRIORITY } from "pixi.js"
import type { Client } from "../client"
import { resolveConfig } from "../runtime_config"
import { AttentionOverlay, parseAttention } from "./attention"
import { recordingLines, ReplaySocket, ReplayStatus } from "./replay_socket"
import { WatchSocket } from "./watch_socket"

/**
 * The game client as the /admin replay VIEWER: `/?replay` inside an iframe on the admin Players page.
 *
 * It plays either a session recording (a ReplaySocket) or a live session (a WatchSocket) into the
 * ordinary client -- the same renderer, netcode and GUI as a player gets -- with input, sound and
 * logging in switched off. The admin page owns everything around it (fetching the recording, the
 * timeline, play/pause/speed) and talks to this frame with postMessage:
 *
 *   frame -> page   { type: "samsara-replay-ready" }        until the page answers with init
 *   page  -> frame  { type: "samsara-replay-init", mode: "replay", recording: ArrayBuffer, startAt?, speed?, playing? }
 *                   { type: "samsara-replay-init", mode: "live", token, sessionId }
 *   page  -> frame  { type: "samsara-replay-control", action: "play" | "pause" | "speed" | "seek", value? }
 *   frame -> page   { type: "samsara-replay-status", ...ReplayStatus, live, endedReason?, contentMismatch, error? }
 *   frame -> page   { type: "samsara-replay-viewport", w, h }   the recorded player's window size, so
 *                   the page can make this frame that size (and the game lay out as it did for them)
 *
 * Only messages from the parent window are taken. The token for a live view travels in a message,
 * never the URL, so it stays out of history and server logs.
 *
 * A replay needs its clock installed BEFORE the client exists (see ReplaySocket on virtual time), so
 * index.ts holds its boot until `ready()`, and calls `connect` instead of logging in.
 */

type InitMessage =
    | { type: "samsara-replay-init"; mode: "replay"; recording: ArrayBuffer; startAt?: number; speed?: number; playing?: boolean }
    | { type: "samsara-replay-init"; mode: "live"; token: string; sessionId: string }

type ControlMessage = { type: "samsara-replay-control"; action: "play" | "pause" | "speed" | "seek"; value?: number }

const STATUS_MS = 250

export const replayRequested = (): boolean => {
    try {
        return new URLSearchParams(window.location.search).has("replay")
    } catch {
        return false
    }
}

class ReplayMode {
    private socket?: ReplaySocket | WatchSocket
    private isReady = false
    private error?: string
    private endedReason?: string
    private header?: { contentRevision?: string }
    private pendingStart?: { startAt?: number; speed?: number; playing?: boolean }
    private attention = new AttentionOverlay()

    ready = (): boolean => this.isReady

    /** Listen for the admin page, and keep telling it this frame is waiting until it answers. */
    begin = () => {
        // Now, before Keyboard.initialize registers anything, so this capture listener runs first.
        this.blockInput()
        window.addEventListener("message", this.onMessage)
        const announce = () => window.parent?.postMessage({ type: "samsara-replay-ready" }, "*")
        announce()
        const again = setInterval(() => (this.socket || this.error ? clearInterval(again) : announce()), 500)
    }

    /** In place of Client.connect: wire the client to the replay or live socket and start it. */
    connect = (client: Client, app: Application) => {
        const socket = this.socket
        if (!socket) return
        client.connectReplay(socket)
        // A viewer must never be heard, and must never drive the player it is watching.
        client.clientGameLogic.audio.setSoundEnabled(false)
        if (socket instanceof ReplaySocket) {
            // Ahead of the client's own update (NORMAL priority), so a frame's messages are drawn in it.
            app.ticker.add(() => socket.advance(app.ticker.elapsedMS), undefined, UPDATE_PRIORITY.HIGH)
            // What each click SENT is recorded, so act it out; and where each click landed, so show it.
            socket.onPlayerSent = text => {
                client.actOutRecordedAnswer(text)
                this.showAttention(text)
            }
            const start = this.pendingStart
            if (start?.speed) socket.setSpeed(start.speed)
            if (start?.playing === false) socket.pause()
            if (start?.startAt) socket.seekTo(start.startAt)
        }
        socket.start()
        setInterval(this.postStatus, STATUS_MS)
    }

    private onMessage = (event: MessageEvent) => {
        if (event.source !== window.parent) return
        const data = event.data
        if (data?.type === "samsara-replay-init" && !this.socket) {
            void this.init(data as InitMessage)
        } else if (data?.type === "samsara-replay-control") {
            this.control(data as ControlMessage)
        }
    }

    private init = async (message: InitMessage) => {
        try {
            if (message.mode === "live") {
                this.socket = new WatchSocket(resolveConfig().apiHost, message.token, message.sessionId, reason => {
                    this.endedReason = reason
                    this.postStatus()
                })
                this.isReady = true
                return
            }
            const socket = new ReplaySocket(recordingLines(message.recording))
            this.header = await socket.open()
            this.socket = socket
            this.pendingStart = { startAt: message.startAt, speed: message.speed, playing: message.playing }
            // The recording's clock, from before the first object that reads the time is built.
            Date.now = () => socket.now()
            this.isReady = true
        } catch (error) {
            this.error = error instanceof Error ? error.message : String(error)
            this.postStatus()
        }
    }

    private showAttention = (text: string) => {
        const attention = parseAttention(text)
        if (attention?.kind === "viewport") {
            this.attention.viewport = { w: attention.w, h: attention.h }
            window.parent?.postMessage({ type: "samsara-replay-viewport", w: attention.w, h: attention.h }, "*")
        } else if (attention?.kind === "click") {
            this.attention.show(attention)
        }
    }

    private control = (message: ControlMessage) => {
        const socket = this.socket
        if (!(socket instanceof ReplaySocket)) return
        if (message.action === "play") socket.play()
        else if (message.action === "pause") socket.pause()
        else if (message.action === "speed" && typeof message.value === "number") socket.setSpeed(message.value)
        else if (message.action === "seek" && typeof message.value === "number") socket.seekTo(message.value)
        this.postStatus()
    }

    private postStatus = () => {
        const socket = this.socket
        const status: Partial<ReplayStatus> = socket instanceof ReplaySocket ? socket.status() : {}
        window.parent?.postMessage(
            {
                type: "samsara-replay-status",
                ...status,
                live: socket instanceof WatchSocket,
                endedReason: this.endedReason,
                error: this.error,
                contentMismatch: !!this.header?.contentRevision && this.header.contentRevision !== contentRevision,
            },
            "*",
        )
    }

    /**
     * Keys and pointer never reach the game. The page can still be scrolled and the frame resized;
     * the admin page puts its controls outside the frame.
     */
    private blockInput = () => {
        const swallow = (event: Event) => event.stopImmediatePropagation()
        for (const type of ["keydown", "keyup", "keypress"]) window.addEventListener(type, swallow, true)
        document.documentElement.style.pointerEvents = "none"
    }
}

export const replayMode = new ReplayMode()
